// ============================================================
// chatEngine.js — AALI GROUP Response Engine
// Keyword detection, intent matching, and response builder.
// ============================================================

import {
  COMPANY_INFO,
  FOUNDER_INFO,
  SUBSIDIARIES,
  INDUSTRIES,
  CAREERS_INFO,
  INVESTORS_INFO,
  VISION_MISSION,
  WHATSAPP_NUMBER,
  WHATSAPP_WELCOME_MSG,
  GLOBAL_LOCATIONS,           // <-- NEW import
  getLocationsSummary,        // <-- NEW import
} from "./chatData";

// ── Utility ──────────────────────────────────────────────────
const normalize = (text) => text.toLowerCase().replace(/[^\w\s]/g, " ");

const containsAny = (text, keywords) =>
  keywords.some((kw) => normalize(text).includes(kw.toLowerCase()));

// ── Find a specific location by city or country ─────────────
const findLocation = (input) => {
  const normalizedInput = normalize(input);
  return GLOBAL_LOCATIONS.find(loc => 
    normalizedInput.includes(loc.city.toLowerCase()) ||
    normalizedInput.includes(loc.country.toLowerCase())
  );
};

// ── Intent Detection ─────────────────────────────────────────
const detectIntent = (input) => {
  const t = normalize(input);

  // Goodbye / bye
  const byes = ["bye", "goodbye", "see you", "see ya", "later", "take care", "good night", "goodnight", "gn", "cya", "tata", "ok bye", "okay bye", "thanks bye", "thank you bye", "thx bye"];
  if (containsAny(t, byes)) return "BYE";

  // Thank you
  const thanks = ["thank you", "thanks", "thank u", "thankyou", "thx", "ty", "appreciated", "great help", "helpful", "awesome", "perfect", "wonderful", "excellent", "nice", "good job", "well done", "amazing", "superb", "brilliant", "fantastic"];
  if (containsAny(t, thanks)) return "THANK_YOU";

  // Greetings
  const greetings = ["hi", "hello", "hey", "good morning", "good evening", "good afternoon", "namaste", "howdy", "hii", "helo", "hai", "ello", "hiya", "sup", "what's up", "whats up", "howdy"];
  if (containsAny(t, greetings) && t.split(" ").length <= 5) return "GREETING";

  // ── LOCATION intent (NEW) ─────────────────────────────────
  // Generic location keywords
  const locKeywords = ["location", "office", "address", "where are you", "global presence", "cities", "countries", "footprint"];
  if (containsAny(t, locKeywords)) return "LOCATIONS";

  // Specific city/country mention
  const specificLoc = findLocation(input);
  if (specificLoc) return "SPECIFIC_LOCATION";

  if (containsAny(t, ["about", "who are you", "what is aali", "tell me about", "company", "overview", "what do you do", "conglomerate", "group"]))
    return "ABOUT";

  if (containsAny(t, ["founder", "ceo", "azagar", "leader", "owner", "chairman"])) return "FOUNDER";

  if (containsAny(t, ["service", "offer", "provide", "what do you", "capabilities"])) return "SERVICES";

  if (containsAny(t, ["business", "subsidiar", "companies", "ventures", "portfolio", "all companies", "list"])) return "BUSINESSES";

  if (containsAny(t, ["career", "job", "hiring", "vacancy", "work", "employment", "internship", "apply", "opportunities", "opening"])) return "CAREERS";

  if (containsAny(t, ["invest", "investor", "funding", "stake", "equity", "partnership", "venture capital", "opportunity"])) return "INVESTORS";

  if (containsAny(t, ["contact", "reach", "phone", "email", "address", "whatsapp", "call", "connect"])) return "CONTACT";

  if (containsAny(t, ["vision", "mission", "value", "goal", "purpose", "philosophy"])) return "VISION";

  if (containsAny(t, ["industry", "sector", "vertical", "domain", "field"])) return "INDUSTRIES";

  // Check for specific subsidiary intent
  for (const sub of SUBSIDIARIES) {
    if (containsAny(t, sub.keywords)) return `SUBSIDIARY_${sub.id.toUpperCase()}`;
  }

  if (containsAny(t, ["support", "help", "issue", "problem", "question", "assist", "query", "trouble"])) return "SUPPORT";

  return "UNKNOWN";
};

// ── WhatsApp link helper ─────────────────────────────────────
export const buildWhatsAppLink = () => {
  const encoded = encodeURIComponent(WHATSAPP_WELCOME_MSG);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;
};

// ── Random pick helper ───────────────────────────────────────
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ── Response Builder ─────────────────────────────────────────
export const generateResponse = (userInput) => {
  const intent = detectIntent(userInput);

  switch (intent) {

    case "GREETING": {
      const replies = [
        `Hey there! 👋 Great to see you at **AALI GROUP**!\n\nI'm your virtual assistant, ready to help you explore our businesses, services, and opportunities. What can I do for you today?`,
        `Hello! 😊 Welcome to **AALI GROUP** — a diversified conglomerate with a vision to build tomorrow, today.\n\nHow can I assist you?`,
        `Hi! 🙌 Good to have you here at **AALI GROUP**!\n\nFeel free to ask me about our companies, careers, investors, or anything else. I'm here to help!`,
        `Namaste! 🙏 Welcome to **AALI GROUP**.\n\nI can help you discover our businesses, explore career opportunities, or connect you with our team. What's on your mind?`,
        `Hey! 👋 Welcome! You've reached the **AALI GROUP** virtual assistant.\n\nAsk me anything — our companies, services, team, or how to get in touch!`,
      ];
      return { text: pick(replies), links: [] };
    }

    case "THANK_YOU": {
      const replies = [
        `You're most welcome! 😊 It was a pleasure assisting you.\n\nIf you have more questions, feel free to ask anytime. We're always here to help at **AALI GROUP**!`,
        `Thank you for your kind words! 🙏 We're glad we could help.\n\nDon't hesitate to reach out if you need anything else from **AALI GROUP**!`,
        `Happy to help! 😄 Your satisfaction means a lot to us at **AALI GROUP**.\n\nIs there anything else I can assist you with?`,
        `Absolutely our pleasure! 🌟 That's what we're here for.\n\nFeel free to explore more about **AALI GROUP** — we'd love to be of further help!`,
        `So glad I could assist! 🎉 Thank you for reaching out to **AALI GROUP**.\n\nAnytime you need help, we're just a message away!`,
      ];
      return {
        text: pick(replies),
        links: [
          { label: "Explore Our Businesses →", route: "/businesses" },
          { label: "Contact Us 💬", whatsapp: true },
        ],
      };
    }

    case "BYE": {
      const replies = [
        `Goodbye! 👋 Thank you so much for visiting **AALI GROUP**.\n\nWe hope to see you again soon. Have a wonderful day! 😊`,
        `Take care! 🙏 It was great chatting with you.\n\nThank you for your interest in **AALI GROUP** — come back anytime, we're always here for you! 🌟`,
        `Bye bye! 👋 Thanks for stopping by **AALI GROUP**.\n\nHave an amazing day ahead, and don't forget — we're always here whenever you need us! 😊`,
        `See you soon! 🌟 Thank you for visiting **AALI GROUP**.\n\nWe hope we were helpful. Come back anytime — we'd love to hear from you again! 👋`,
        `Goodbye and thank you! 🙏 Your visit means a lot to us at **AALI GROUP**.\n\nWishing you a great day — see you next time! ✨`,
      ];
      return {
        text: pick(replies),
        links: [
          { label: "Visit Us Again →", route: "/" },
          { label: "WhatsApp Us Anytime 💬", whatsapp: true },
        ],
      };
    }

    // ── NEW: Generic Locations Response ──────────────────────
    case "LOCATIONS":
      return {
        text: `${getLocationsSummary()}\n\nWould you like details about a specific city? Just ask me like “Tell me about Hyderabad office”.`,
        links: [
          { label: "View All Office Locations →", route: "/global-locations" },
          { label: "Contact Us 💬", whatsapp: true },
        ],
      };

    // ── NEW: Specific Location Response ──────────────────────
    case "SPECIFIC_LOCATION": {
      const location = findLocation(userInput);
      if (location) {
        return {
          text: `**${location.flag} ${location.city}, ${location.country}** — ${location.type}\n\n📍 **Address:** ${location.address}\n📞 **Phone:** ${location.phone}\n✉️ **Email:** ${location.email}\n\nThis office serves as a key hub for AALI GROUP's operations in the region.`,
          links: [
            { label: "View All Locations →", route: "/global-locations" },
            { label: "Contact via WhatsApp 💬", whatsapp: true },
          ],
        };
      }
      // fallback (should not happen because intent already matched)
      return {
        text: `I couldn't find details for that location. Please check our global locations page for all offices.`,
        links: [{ label: "View All Locations →", route: "/global-locations" }],
      };
    }

    case "ABOUT":
      return {
        text: `**${COMPANY_INFO.name}** is a diversified conglomerate headquartered in **Guntur, Andhra Pradesh, India**.\n\nWe operate across **8 subsidiary companies** spanning:\n• 🖥️ Technology & Consulting\n• 🏗️ Infrastructure\n• 🎬 Media & Entertainment\n• 💎 Luxury Retail\n• 🤝 Social Impact\n\nWith **300+ jobs created** and a vision to create sustainable value across industries, AALI GROUP stands as a pillar of innovation, excellence, and responsibility.`,
        links: [{ label: "Learn More →", route: "/about" }],
      };

    case "FOUNDER":
      return {
        text: `**${FOUNDER_INFO.name}** is the **${FOUNDER_INFO.title}** of AALI GROUP.\n\nWith **${FOUNDER_INFO.experience}** of entrepreneurial experience, he has built AALI GROUP into a respected conglomerate across technology, infrastructure, media, luxury retail, and social impact.\n\n*"${FOUNDER_INFO.vision}"*`,
        links: [{ label: "Meet the Founder →", route: "/founder" }],
      };

    case "VISION":
      return {
        text: `**Our Vision** 🎯\n${VISION_MISSION.vision}\n\n**Our Mission** 🚀\n${VISION_MISSION.mission}`,
        links: [{ label: "About AALI GROUP →", route: "/about" }],
      };

    case "SERVICES": {
      const allServices = SUBSIDIARIES.map(
        (s) => `• **${s.name}** — ${s.services.slice(0, 3).join(", ")}`
      ).join("\n");
      return {
        text: `AALI GROUP delivers services across multiple domains:\n\n${allServices}\n\nWould you like to know more about a specific company or service?`,
        links: [{ label: "Explore All Businesses →", route: "/businesses" }],
      };
    }

    case "BUSINESSES": {
      const list = SUBSIDIARIES.map((s) => `• **${s.name}** — ${s.industry}`).join("\n");
      return {
        text: `AALI GROUP has **${SUBSIDIARIES.length} subsidiary companies**:\n\n${list}\n\nAsk me about any specific company for detailed information!`,
        links: [{ label: "View All Companies →", route: "/businesses" }],
      };
    }

    case "INDUSTRIES": {
      const list = INDUSTRIES.map(
        (ind) => `• **${ind.name}** — ${ind.companies.join(", ")}`
      ).join("\n");
      return {
        text: `AALI GROUP operates across **${INDUSTRIES.length} key industries**:\n\n${list}`,
        links: [{ label: "Explore Industries →", route: "/businesses" }],
      };
    }

    case "CAREERS":
      return {
        text: `💼 **Careers at AALI GROUP**\n\nWe're always looking for passionate, driven professionals to join our growing family!\n\n**Benefits of working with us:**\n• ${CAREERS_INFO.benefits.join("\n• ")}\n\nWe hire across all business verticals — technology, infrastructure, media, consulting, and social impact.\n\n**How to apply:** ${CAREERS_INFO.howToApply}`,
        links: [
          { label: "View Careers Page →", route: "/careers" },
          { label: "WhatsApp Us 💬", whatsapp: true },
        ],
      };

    case "INVESTORS":
      return {
        text: `📈 **Investment Opportunities at AALI GROUP**\n\n${INVESTORS_INFO.description}\n\n**Key sectors:**\n• ${INVESTORS_INFO.sectors.join("\n• ")}\n\n${INVESTORS_INFO.contact}`,
        links: [
          { label: "Investor Relations →", route: "/investors" },
          { label: "Contact Us 💬", whatsapp: true },
        ],
      };

    case "CONTACT":
      return {
        text: `📞 **Get in Touch with AALI GROUP**\n\n📧 **Email:** ${COMPANY_INFO.email}\n📱 **Phone/WhatsApp:** ${COMPANY_INFO.phone}\n☎️ **Landline:** ${COMPANY_INFO.landline}\n📍 **Location:** ${COMPANY_INFO.address}\n\nOur team is available to answer your queries about any of our businesses or services!`,
        links: [
          { label: "Contact Page →", route: "/contact" },
          { label: "Chat on WhatsApp 💬", whatsapp: true },
        ],
      };

    case "SUPPORT":
      return {
        text: `🤝 **We're here to help!**\n\nFor support, queries, or any assistance, you can reach us through:\n\n📧 **Email:** ${COMPANY_INFO.email}\n📱 **WhatsApp:** ${COMPANY_INFO.phone}\n\nOur team typically responds within 24 hours. For instant support, WhatsApp is the fastest way to reach us!`,
        links: [
          { label: "WhatsApp Support 💬", whatsapp: true },
          { label: "Contact Page →", route: "/contact" },
        ],
      };

    default: {
      // Check for subsidiary-specific responses
      if (intent.startsWith("SUBSIDIARY_")) {
        const subId = intent.replace("SUBSIDIARY_", "").toLowerCase();
        const sub = SUBSIDIARIES.find((s) => s.id === subId);
        if (sub) {
          return {
            text: `**${sub.name}** — ${sub.industry}\n\n${sub.description}\n\n**Services:**\n• ${sub.services.join("\n• ")}${sub.externalSite ? `\n\n🌐 Visit: ${sub.externalSite}` : ""}`,
            links: [
              { label: `Explore ${sub.name} →`, route: sub.route },
              { label: "Contact Us 💬", whatsapp: true },
            ],
          };
        }
      }

      // Fallback — unknown intent
      return {
        text: `Hmm, I'm not quite sure about that. 🤔\n\nHere are some things I can help you with:\n• Our businesses & services\n• Career opportunities\n• Investment details\n• Global office locations\n• Contact information\n• About our founder\n\nOr you can reach us directly on WhatsApp for a personalized response!`,
        links: [
          { label: "Contact Page →", route: "/contact" },
          { label: "WhatsApp Us 💬", whatsapp: true },
        ],
      };
    }
  }
};